import { v4 as uuidv4 } from "uuid";

import { type GetLabelRulesByGroupV3_me_labelRulesByGroupV3 } from "@north-beam/nb-client-side";
import {
  compileRegex,
  extractRegexCaptures,
  segmentAsJSON,
  validateOutputTemplate,
  type BreakdownGroupDescriptor,
  type LabelFilter,
} from "@north-beam/nb-common";

import {
  type AdvancedBreakdownDescriptor,
  type AdvancedSegmentDescriptor,
  type AdvancedSegmentLevel,
  type TemplateDetail,
} from "./types";

export function createEmptyMatcher() {
  return { and: [{ "==": [{ var: "name" }, ""] }] };
}

export function createEmptyAdvancedSegment(): AdvancedSegmentDescriptor {
  return {
    id: uuidv4(),
    level: "campaign",
    name: "",
    output: "",
    active: true,
    priority: 10,
    matcher: createEmptyMatcher(),
    errors: {
      name: null,
      output: null,
      priority: null,
      matcher: null,
      level: null,
    },
  };
}

export function createEmptyAdvancedBreakdown(): AdvancedBreakdownDescriptor {
  return {
    groupId: uuidv4(),
    name: "My new breakdown",
    segments: [],
    error: null,
  };
}

export function simpleBreakdownToAdvancedBreakdown(
  simpleBreakdown: BreakdownGroupDescriptor,
  labelFilters: LabelFilter[],
): AdvancedBreakdownDescriptor {
  const segments: AdvancedSegmentDescriptor[] = [];

  simpleBreakdown.segments
    .filter((e) => e.name !== "")
    .forEach((segment, idx) => {
      const levels: AdvancedSegmentLevel[] =
        segment.level === "all" ? ["campaign", "adset", "ad"] : [segment.level];

      const segmentJSON = segmentAsJSON(segment, labelFilters);

      levels.forEach((level) => {
        segments.push({
          id: segment.id,
          level: level,
          name: segment.name.trim(),
          output: segment.name.trim(),
          active: segment.active,
          priority: idx + 1,
          matcher: segmentJSON.matcher,
          createdAt: segment.createdtAt,
          errors: {
            name: null,
            output: null,
            priority: null,
            matcher: null,
            level: null,
          },
        });
      });
    });

  return {
    groupId: uuidv4(),
    name: simpleBreakdown.name,
    segments,
    error: null,
  };
}

export function labelRulesToAdvancedBreakdown(
  labelRules: GetLabelRulesByGroupV3_me_labelRulesByGroupV3[],
): AdvancedBreakdownDescriptor {
  const segments: AdvancedSegmentDescriptor[] = [];

  labelRules.forEach((labelRule) => {
    labelRule.labelRule.forEach((rule) => {
      segments.push({
        id: uuidv4(),
        level: labelRule.level as AdvancedSegmentLevel,
        name: rule.name.trim(),
        output: rule.output.trim(),
        active: rule.active,
        priority: rule.priority,
        matcher: rule.matcher,
        createdAt: rule.createdAt,
        errors: {
          name: null,
          output: null,
          priority: null,
          matcher: null,
          level: null,
        },
      });
    });
  });

  return {
    groupId: labelRules[0].groupId,
    name: labelRules[0].name,
    segments,
    error: null,
  };
}

interface LabelRule {
  name: string;
  output: string;
  active: boolean;
  priority: number;
  matcher: any;
  createdAt?: string;
}

export function advancedBreakdownToLabelRules(
  breakdown: AdvancedBreakdownDescriptor,
) {
  const campaigns: LabelRule[] = [];
  const adsets: LabelRule[] = [];
  const ads: LabelRule[] = [];

  breakdown.segments.forEach((segment) => {
    const labelRule = {
      name: segment.name.trim(),
      output: segment.output.trim(),
      active: segment.active,
      priority: segment.priority,
      matcher: segment.matcher,
      createdAt: segment.createdAt,
    };

    if (segment.level === "campaign") {
      campaigns.push(labelRule);
    }

    if (segment.level === "adset") {
      adsets.push(labelRule);
    }

    if (segment.level === "ad") {
      ads.push(labelRule);
    }
  });

  return {
    campaign:
      campaigns.length > 0
        ? {
            name: breakdown.name.trim(),
            level: "campaign",
            labelRules: campaigns,
            isUsingSimpleUI: false,
          }
        : undefined,
    adset:
      adsets.length > 0
        ? {
            name: breakdown.name.trim(),
            level: "adset",
            labelRules: adsets,
            isUsingSimpleUI: false,
          }
        : undefined,
    ad:
      ads.length > 0
        ? {
            name: breakdown.name.trim(),
            level: "ad",
            labelRules: ads,
            isUsingSimpleUI: false,
          }
        : undefined,
  };
}

export function validateAdvancedSegment(
  segment: AdvancedSegmentDescriptor,
): AdvancedSegmentDescriptor {
  const errors: AdvancedSegmentDescriptor["errors"] = {
    name: null,
    output: null,
    priority: null,
    matcher: null,
    level: null,
  };

  if (!segment.name) {
    errors.name = "Required, rule name";
  }

  if (!segment.output.trim()) {
    errors.output = "Required, output label value";
  }

  if (!segment.priority) {
    errors.priority = "Required, rank";
  }

  if (isNaN(parseInt(segment.priority.toString()))) {
    errors.priority = "Not a number, rank";
  }

  if (!segment.level) {
    errors.level = "Required, level";
  }

  if (!segment.matcher) {
    errors.matcher = "Required, matcher";
  }

  if (segment.matcher) {
    const outputError = validateOutputTemplate(
      segment.output.trim(),
      getTemplateDetails(segment.matcher).flatMap((v) => v.templates),
    );

    if (outputError) {
      errors.output = outputError;
    }
  }

  return { ...segment, errors };
}

export function validateAdvancedBreakdown(
  breakdown: AdvancedBreakdownDescriptor,
  labelNames: string[],
): AdvancedBreakdownDescriptor {
  let error: string | null = null;

  if (!breakdown.name) {
    error = "Required, breakdown name";
  }

  if (labelNames.includes(breakdown.name)) {
    error = "Duplicate, breakdown name";
  }

  if (breakdown.segments.length === 0) {
    error = "Required, breakdown segment";
  }

  return { ...breakdown, error };
}

export function getTemplateDetails(matcher: any): TemplateDetail[] {
  const rv = [
    {
      description: "Name",
      templates: ["data.name"],
    },
    {
      description: "NBT params",
      templates: ["data.nbt.platform", "data.nbt.campaignName"],
    },
    {
      description: "UTM params",
      templates: [
        "data.utm.source",
        "data.utm.medium",
        "data.utm.campaign",
        "data.utm.term",
        "data.utm.content",
      ],
    },
    {
      description: "AdKey params",
      templates: [
        "data.adKey.campaign",
        "data.adKey.kind",
        "data.adKey.platform",
      ],
    },
    {
      description: "Referral params",
      templates: ["data.referral.domain"],
    },
  ];

  const regexps = extractRegexCaptures(matcher || {});

  regexps.forEach(({ pattern }, i) => {
    const description = `<div>Regex pattern:<div><code class="text-venus">${pattern}</code>`;

    try {
      const xre = compileRegex(pattern);

      const templates = [
        "__root",
        ...((xre as any).xregexp?.captureNames ?? []),
      ].map((v) => `matches.${i}.${v}`);

      rv.push({ description, templates });
    } catch (e) {
      rv.push({ description, templates: [] });
    }
  });

  return rv;
}
